import { useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import {
  ArrowTopRightOnSquareIcon,
  CheckBadgeIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import certifiedpassed from '../src/certified-passed.png'
import urbrev from '../src/urbrev-logo.png'
import kronospaket from '../src/kronos-certified.png'

const navigation = [
  { name: 'Kronos köpskydd', href: '/kronos-secure' },
  { name: 'Kronos Certified', href: '/kronos-certified' },
  { name: 'Kontrollera certifiering', href: '/kronos-certified-control' },
  { name: 'Klockvärdering', href: '/klockvardering' },
  { name: 'Företagsannonsering', href: '/foretagsannonsering' },
  { name: 'Marknadsföring', href: '/marknadsforing' },
  { name: 'Blogg', href: '/blogg' },
  { name: 'Integritetspolicy', href: '/integritetspolicy' },
  { name: 'Användarvillkor', href: '/anvandarvillkor' },
]

function KronosCertifiedControl({ siteLogo }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="fixed inset-x-0 top-0 z-50 bg-white shadow">
        <nav className="flex items-center justify-between p-6 lg:px-8">
          <a href="/" className="flex items-center -m-1.5 p-1.5">
            <span className="sr-only">Krono</span>
            <img alt="Krono Logo" src={siteLogo} className="h-6 w-auto rounded-md" />
          </a>

          <div className="flex lg:hidden">
            <button
              type="button"
              className="p-2.5 text-gray-700 rounded-md"
              onClick={() => setMobileMenuOpen(true)}
            >
              <span className="sr-only">Open menu</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                />
              </svg>
            </button>
          </div>

          <div className="hidden lg:flex lg:gap-x-8">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-sm font-semibold text-gray-900 hover:underline"
              >
                {item.name}
              </a>
            ))}
          </div>
        </nav>

        {/* Mobile menu */}
        <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
          <div className="fixed inset-0 z-50" />
          <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-white px-6 py-6 overflow-y-auto shadow-lg">
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="p-2.5 text-gray-700 rounded-md"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 space-y-2">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block rounded-lg px-3 py-2 text-base font-semibold text-gray-900 hover:bg-gray-50"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </DialogPanel>
        </Dialog>
      </header>

      <main className="px-5 lg:px-10 bg-white">
        <div className="mx-auto max-w-6xl">
          <section className="mx-auto max-w-3xl text-center py-8">
            <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-semibold text-blue-700 ">
              <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" />
              Tryggare klockaffärer med Kronos Certified
            </div>

            <h1 className="mt-7 text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
              Kontrollera en
              <span className="mt-1 block text-blue-700">Kronos Certified klocka</span>
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-base leading-8 text-slate-600 sm:text-lg">
              Ska du köpa en Kronos Certified klocka? Kontrollera certifieringsresultatet hos UrBrev
              innan du genomför köpet.
            </p>
          </section>

          <section className="mx-auto mt-12 max-w-5xl overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-xl shadow-slate-900/[0.06] sm:mt-16">
            <div className="grid md:grid-cols-2">
              <div className="relative min-h-[280px] overflow-hidden bg-white  md:min-h-[520px]">
                <img
                  src={kronospaket}
                  alt="Exempel på godkänt Kronos Certified resultat"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950/65 via-slate-950/20 to-transparent p-6 pt-20 sm:p-8 sm:pt-24">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/15 px-3 py-1.5 text-sm font-semibold text-white backdrop-blur-md">
                    <CheckBadgeIcon className="h-5 w-5" aria-hidden="true" />
                    Certifieringsresultat
                  </div>
                  <p className="mt-3 max-w-sm text-xl font-semibold leading-snug text-white sm:text-2xl">
                    Kontrollera uppgifterna innan du köper
                  </p>
                </div>
              </div>

              <div className="flex flex-col justify-center p-6 sm:p-9 lg:p-11">
                <div className="mb-7 flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="text-sm font-medium text-slate-500">I samarbete med</span>
                  <img
                    src={urbrev}
                    alt="UrBrev"
                    className="h-7 w-auto max-w-[140px] object-contain"
                  />
                </div>

                <h2 className="text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                  Verifiera certifieringen
                </h2>
                <p className="mt-4 leading-7 text-slate-600">
                  Be säljaren om{' '}
                  <strong className="font-semibold text-slate-900">ärendenumret</strong> som står på
                  baksidan av Kronos Certified certifikatet.
                </p>

                <div className="mt-5 rounded-2xl border border-blue-100 bg-blue-50/70 p-4">
                  <p className="text-sm font-semibold text-blue-950">
                    Så känner du igen ärendenumret
                  </p>
                  <p className="mt-1.5 text-sm leading-6 text-blue-900/80">
                    Ärendenumret börjar alltid med{' '}
                    <strong className="font-bold text-blue-950">KC</strong> följt av siffror.
                  </p>
                </div>

                <p className="mt-5 leading-7 text-slate-600">
                  Använd numret för att kontrollera klockans certifieringsresultat hos vår
                  samarbetspartner UrBrev.
                </p>

                <a
                  href="https://www.urbrev.se/kronoscertified.html#kontrollera"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 px-6 py-4 text-base font-semibold text-white shadow-lg shadow-blue-700/20 transition hover:-translate-y-0.5 hover:bg-blue-800 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-200"
                >
                  Kontrollera certifiering hos UrBrev
                  <ArrowTopRightOnSquareIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                </a>
                <p className="mt-3 text-center text-xs leading-5 text-slate-400">
                  Du skickas vidare till UrBrevs webbplats för kontrollen.
                </p>
              </div>
            </div>
          </section>

          <section className="mx-auto mt-14 grid max-w-5xl gap-5 sm:mt-16 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm shadow-slate-900/[0.03]">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                <span className="text-lg font-bold">1</span>
              </div>
              <h3 className="mt-5 text-lg font-bold text-slate-900">Be om ärendenumret</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Be säljaren om numret som finns på baksidan av certifikatet.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm shadow-slate-900/[0.03]">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                <span className="text-lg font-bold">2</span>
              </div>
              <h3 className="mt-5 text-lg font-bold text-slate-900">Öppna UrBrev</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Gå till UrBrevs kontroll för Kronos Certified.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm shadow-slate-900/[0.03]">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                <span className="text-lg font-bold">3</span>
              </div>
              <h3 className="mt-5 text-lg font-bold text-slate-900">Kontrollera resultatet</h3>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Ange ärendenumret och kontrollera certifieringsuppgifterna innan köpet.
              </p>
            </div>
          </section>

          <section className="mx-auto mt-12 max-w-5xl rounded-3xl border border-slate-200/80 bg-white px-6 py-8 text-center sm:mt-14 sm:px-10 sm:py-10 mb-8">
            <h2 className="text-2xl font-bold tracking-tight text-slate-950">
              Kontrollera innan du köper
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-sm leading-7 text-slate-600 sm:text-base">
              Kronos Certified gör det möjligt att kontrollera certifieringsresultatet för en klocka
              innan ett köp genomförs. Be säljaren om ärendenumret på certifikatet och använd det
              hos UrBrev för att kontrollera uppgifterna från certifieringen.
            </p>
            <a
              href="https://www.urbrev.se/kronoscertified.html#kontrollera"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-blue-700 transition hover:bg-blue-50"
            >
              Gå till UrBrev
              <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
            </a>
          </section>
        </div>
      </main>
    </div>
  )
}

export default KronosCertifiedControl
